import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  FaUsers, 
  FaEdit, 
  FaTrash, 
  FaPlus, 
  FaTimes,
  FaUserShield,
  FaUser
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';

const UserManagementContainer = styled.div`
  padding: 2rem;
  background: #f8f9fa;
  min-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #667eea;
`;

const Title = styled.h1`
  color: #333;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AddUserButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.75rem 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  
  &:hover {
    background: #218838;
  }
`;

const UserGrid = styled.div`
  display: grid;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const UserCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserName = styled.h3`
  margin: 0 0 0.5rem 0;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const UserEmail = styled.p`
  margin: 0 0 0.25rem 0;
  color: #666;
  font-size: 0.9rem;
`;

const UserDetails = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 0.8rem;
  color: #888;
`;

const UserStatus = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  
  &.active {
    background: #d4edda;
    color: #155724;
  }
  
  &.inactive {
    background: #f8d7da;
    color: #721c24;
  }
  
  &.superuser {
    background: #fff3cd;
    color: #856404;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  transition: all 0.2s;
  
  &.edit {
    background: #007bff;
    color: white;
    
    &:hover {
      background: #0056b3;
    }
  }
  
  &.delete {
    background: #dc3545;
    color: white;
    
    &:hover {
      background: #c82333;
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e0e0e0;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: #333;
  font-weight: 600;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const Checkbox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  input[type="checkbox"] {
    width: auto;
  }
`;

const Alert = styled.div`
  padding: 1rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  
  &.error {
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  }
  
  &.success {
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const UserManagement = ({ onClose }) => {
  const { user, users, loadingUsers, getUsers, updateUser, deleteUser, error } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    full_name: '',
    is_active: true,
    is_superuser: false
  });

  useEffect(() => {
    if (user?.is_superuser) {
      getUsers();
    }
  }, [getUsers, user?.is_superuser]);

  // Check if current user is superuser
  if (!user?.is_superuser) {
    return (
      <UserManagementContainer>
        <Alert className="error">
          Access denied. Only superusers can manage users.
        </Alert>
      </UserManagementContainer>
    );
  }

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      username: user.username,
      full_name: user.full_name,
      is_active: user.is_active,
      is_superuser: user.is_superuser
    });
    setShowModal(true);
  };

  const handleDeleteUser = async (userId) => {
    if (userId === user.id) {
      alert('You cannot delete your own account');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteUser(userId);
      } catch (error) {
        // Error is handled by the auth context
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await updateUser(editingUser.id, formData);
      setShowModal(false);
      setEditingUser(null);
    } catch (error) {
      // Error is handled by the auth context
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      email: '',
      username: '',
      full_name: '',
      is_active: true,
      is_superuser: false
    });
  };

  return (
    <UserManagementContainer>
      <Header>
        <Title>
          <FaUsers />
          User Management
        </Title>
        {onClose && (
          <CloseButton onClick={onClose}>
            <FaTimes />
          </CloseButton>
        )}
      </Header>

      {error && <Alert className="error">{error}</Alert>}

      {loadingUsers ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <LoadingSpinner />
          <p>Loading users...</p>
        </div>
      ) : (
        <UserGrid>
          {users.map((userItem) => (
            <UserCard key={userItem.id}>
              <UserInfo>
                <UserName>
                  {userItem.is_superuser ? <FaUserShield /> : <FaUser />}
                  {userItem.full_name}
                </UserName>
                <UserEmail>{userItem.email}</UserEmail>
                <UserDetails>
                  <span>Username: {userItem.username}</span>
                  <span>Created: {new Date(userItem.created_at).toLocaleDateString()}</span>
                </UserDetails>
              </UserInfo>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-end' }}>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <UserStatus className={userItem.is_active ? 'active' : 'inactive'}>
                    {userItem.is_active ? 'Active' : 'Inactive'}
                  </UserStatus>
                  {userItem.is_superuser && (
                    <UserStatus className="superuser">Superuser</UserStatus>
                  )}
                </div>
                
                <ActionButtons>
                  <Button 
                    className="edit" 
                    onClick={() => handleEditUser(userItem)}
                    disabled={userItem.id === user.id}
                  >
                    <FaEdit />
                    Edit
                  </Button>
                  <Button 
                    className="delete" 
                    onClick={() => handleDeleteUser(userItem.id)}
                    disabled={userItem.id === user.id}
                  >
                    <FaTrash />
                    Delete
                  </Button>
                </ActionButtons>
              </div>
            </UserCard>
          ))}
        </UserGrid>
      )}

      {showModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Edit User</ModalTitle>
              <CloseButton onClick={handleCloseModal}>&times;</CloseButton>
            </ModalHeader>

            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Username</Label>
                <Input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Full Name</Label>
                <Input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Status</Label>
                <Select
                  value={formData.is_active ? 'true' : 'false'}
                  onChange={(e) => setFormData({...formData, is_active: e.target.value === 'true'})}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Checkbox>
                  <input
                    type="checkbox"
                    id="is_superuser"
                    checked={formData.is_superuser}
                    onChange={(e) => setFormData({...formData, is_superuser: e.target.checked})}
                  />
                  <Label htmlFor="is_superuser">Superuser privileges</Label>
                </Checkbox>
              </FormGroup>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <Button type="button" onClick={handleCloseModal}>
                  <FaTimes />
                  Cancel
                </Button>
                <Button type="submit" className="edit">
                  <FaEdit />
                  Update User
                </Button>
              </div>
            </Form>
          </ModalContent>
        </Modal>
      )}
    </UserManagementContainer>
  );
};

export default UserManagement; 